import { trackableFields } from "../components/web-form";
import { trackPath } from "../../helpers";
import { getStashed, removeStashed, setStashed } from "./helpers";
import { ATTRIBUTION_PARAM } from "../components/sections/snowflake/navigation";
import { getReferrerAttribution } from "./referrer";

export const COOKIE_NAME = "attribution";
export const HAS_SIGNUP_CLICK_STASH = "action.signupClick";
export const FIRST_SEEN_STASH = "origin.created";

export const simpleDate = (dateObj) => {
  const date = new Date(dateObj);

  return (
    date.getUTCFullYear() +
    "-" +
    ("0" + (date.getUTCMonth() + 1)).slice(-2) +
    "-" +
    (
      "0" +
      (date.getUTCHours() > 12 ? date.getUTCDate() + 1 : date.getUTCDate())
    ).slice(-2)
  );
};

export const getSimpleDate = (id, storage) =>
  storage.getItem(id)
    ? simpleDate(storage.getItem(id).replace(/"/g, ""))
    : null;

export const clientAttribution = ({
  storage,
  isActive,
  callback,
  referrer,
}) => {
  if (typeof window === "undefined") return;

  const urlParams = new URLSearchParams(window.location.search);
  let newParams = new URLSearchParams();

  // custom param for website click tracking
  const webAttr = urlParams.get(ATTRIBUTION_PARAM);
  if (webAttr) {
    newParams.set(ATTRIBUTION_PARAM, webAttr);
  }
  // debug convenience visibilty param
  if (urlParams.has("show")) {
    newParams.set("show", true);
    storage.setItem("show", true);
  }

  // stash referrer and retrieve utm inferences
  const referrerAttribution = getReferrerAttribution({ storage, referrer });

  // stash first seen date
  if (!storage.getItem(FIRST_SEEN_STASH)) {
    storage.setItem(FIRST_SEEN_STASH, new Date().toISOString());
  }

  // any utm params
  if (trackableFields.find((id) => urlParams.has(`utm_${id}`))) {
    const skipOrigin = trackableFields.find((id) =>
      storage.getItem(`origin.urlParams.${id}`)
    );

    trackableFields.forEach((id) => {
      const value = urlParams.get(`utm_${id}`);

      if (value) {
        newParams.set(`utm_${id}`, value);
        setStashed({
          storage,
          id: `urlParams.${id}`,
          value: value,
          skipOrigin,
        });
      } else {
        // ensure no mis-match combos
        removeStashed({ id: `urlParams.${id}`, storage });
      }
    });
  } else if (isActive("postReferrerAttribution") && referrerAttribution) {
    // use referrer to infer utms
    trackableFields.forEach((id) => {
      const value = referrerAttribution[id];
      if (value) {
        newParams.set(`utm_${id}`, value);
        setStashed({ storage, id: `urlParams.${id}`, value: value });
      } else {
        removeStashed({ id: `urlParams.${id}`, storage });
      }
    });
  } else if (isActive("postStashedParams")) {
    // use stashed utm values
    trackableFields.forEach((id) => {
      const value = getStashed({ storage, id: `urlParams.${id}` });
      if (value) {
        newParams.set(
          `utm_${id}`,
          getStashed({ storage, id: `urlParams.${id}` })
        );
      }
    });
  }

  callback(newParams, storage);
};

export const attributionCookie = (storage) =>
  [
    // recent utm params
    ["utm_parameter___medium", "urlParams.medium"],
    ["utm_parameter___source", "urlParams.source"],
    ["utm_parameter___campaign", "urlParams.campaign"],
    ["utm_parameter___content", "urlParams.content"],
    ["utm_parameter___term", "urlParams.term"],
    // original utm params
    ["utm_parameter___medium_original", "origin.urlParams.medium"],
    ["utm_parameter___source_original", "origin.urlParams.source"],
    ["utm_parameter___campaign_original", "origin.urlParams.campaign"],
    ["utm_parameter___content_original", "origin.urlParams.content"],
    ["utm_parameter___term_original", "origin.urlParams.term"],
    // absolute first seen date
    // [
    //   "first_seen_on_website",
    //   {
    //     value: getSimpleDate(FIRST_SEEN_STASH, storage),
    //   },
    // ],
    // utm dates
    [
      "utm_parameter___created_date",
      {
        value: getSimpleDate(
          `urlParams.${trackableFields.find((id) =>
            getStashed({ id: `urlParams.${id}`, storage })
          )}.created`,
          storage
        ),
      },
    ],
    [
      "utm_parameter___created_date_original",
      {
        value: getSimpleDate(
          `origin.urlParams.${trackableFields.find((id) =>
            storage.getItem(`origin.urlParams.${id}`)
          )}.created`,
          storage
        ),
      },
    ],
  ].reduce((cookie, f) => {
    const value = typeof f[1] === "object" ? f[1].value : storage.getItem(f[1]);
    const addition = value
      ? {
        [f[0]]: value,
      }
      : {};

    return {
      ...cookie,
      ...addition,
    };
  }, {});

const CHAT_SESSION_GAP_MS = 24 * 60 * 60 * 1000;

export const countChatbotMessages = () => {
  let total = 0;
  try {
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (!key?.startsWith("chat-history-")) continue;
      const stored = localStorage.getItem(key);
      if (!stored) continue;
      const messages = JSON.parse(JSON.parse(stored));
      if (Array.isArray(messages)) {
        total += messages.filter((m) => m.who === "user").length;
      }
    }
  } catch (e) {}
  return total;
};

export const getChatbotGoal = () => {
  try {
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (!key?.startsWith("chat-goal-")) continue;
      const stored = localStorage.getItem(key);
      if (!stored) continue;
      const goal = JSON.parse(stored);
      if (goal) return goal;
    }
  } catch (e) {}
  return null;
};

export const getChatbotTranscript = (maxLength = 2000) => {
  try {
    const allMessages = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (!key?.startsWith("chat-history-")) continue;
      const stored = localStorage.getItem(key);
      if (!stored) continue;
      const messages = JSON.parse(JSON.parse(stored));
      if (Array.isArray(messages)) allMessages.push(...messages);
    }
    if (allMessages.length === 0) return "";
    allMessages.sort((a, b) => new Date(a.datetime) - new Date(b.datetime));
    const transcript = allMessages
      .map((m) => `${m.who === "user" ? "YOU" : "BOT"}: ${m.text}`)
      .join("\n\n");
    if (transcript.length <= maxLength) return transcript;
    return "..." + transcript.slice(-(maxLength - 3));
  } catch (e) {}
  return "";
};

export const countChatbotSessions = () => {
  try {
    const userMessages = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (!key?.startsWith("chat-history-")) continue;
      const stored = localStorage.getItem(key);
      if (!stored) continue;
      const messages = JSON.parse(JSON.parse(stored));
      if (Array.isArray(messages)) {
        messages.forEach((m) => {
          if (m.who === "user" && m.datetime) userMessages.push(m);
        });
      }
    }
    if (userMessages.length === 0) return 0;
    userMessages.sort((a, b) => new Date(a.datetime) - new Date(b.datetime));
    let sessions = 1;
    for (let i = 1; i < userMessages.length; i++) {
      if (new Date(userMessages[i].datetime) - new Date(userMessages[i - 1].datetime) > CHAT_SESSION_GAP_MS) {
        sessions++;
      }
    }
    return sessions;
  } catch (e) {}
  return 0;
};

export const getChatbotPath = () => {
  try {
    const stored = localStorage.getItem("chat-path");
    return stored ? JSON.parse(stored) : null;
  } catch (e) {}
  return null;
};

export const appendUtms = ({ dom, urlParams, doc, storage }) => {
  dom.querySelectorAll("a[href*='/demo'], a[href*='/signup'], a[href*='/contact']").forEach((l) => {
    const url = new URL(l.href);
    const onclick = l.onclick;

    for (const [k, v] of urlParams) {
      url.searchParams.set(trackableFields.includes(k) ? `utm_${k}` : k, v);
    }
    if (!url.searchParams.has(ATTRIBUTION_PARAM)) {
      url.searchParams.set(ATTRIBUTION_PARAM, trackPath(doc));
    }

    l.href = url.toString();
    l.onclick = (e) => {
      storage.setItem(HAS_SIGNUP_CLICK_STASH, true);
      if (onclick) {
        onclick(e);
      }
    };
  });
};
